/**
 * The sign test: an exact binomial test on the signs of paired differences, or of a
 * sample's deviations from a hypothesised median.
 *
 * Each difference is positive, negative, or zero. Zeros carry no information about the
 * direction and are dropped, leaving n = n⁺ + n⁻ signed differences. If the median
 * difference is zero, a signed difference is positive with probability ½, so n⁺ follows
 * Binomial(n, ½):
 *
 * - greater (median > 0): P(X ≥ n⁺); less (median < 0): P(X ≤ n⁺);
 * - two-sided: min(1, 2 · min(P(X ≤ n⁺), P(X ≥ n⁺))), which equals R's `binom.test(n⁺, n)`
 *   and `scipy.stats.binomtest(n⁺, n)` at p = ½ because the distribution is symmetric.
 *
 * The share of positive differences, n⁺/n, comes with an exact Clopper–Pearson interval.
 * The interval for the *median* is distribution-free: with the N differences sorted,
 * [x₍ₖ₎, x₍N+1−k₎] covers the population median with probability 1 − 2·P(X ≤ k − 1) for
 * X ~ Binomial(N, ½). The widest such interval at or above the confidence level is not
 * always attainable; this reports the narrowest one that reaches it, and its actual
 * coverage (the same non-interpolated interval as BSDA's `SIGN.test` "Achieved CI" rows).
 */

import { median } from "@/lib/stats/descriptive";
import { binomialUpper } from "@/lib/stats/discrete-tails";
import { binomialCdf, normalUpper } from "@/lib/stats/distributions";
import type { Alternative } from "@/lib/tool-kit/stats-tests/format";
import { clopperPearson } from "@/lib/tool-kit/stats-tests/mcnemar";

export interface MedianInterval {
  lower: number;
  upper: number;
  /** The interval's actual coverage, at least the requested level. */
  coverage: number;
  /** Order-statistic index k: the interval is [x₍ₖ₎, x₍N+1−k₎]. */
  k: number;
}

export interface SignTestResult {
  /** All differences, including zeros. */
  total: number;
  nPositive: number;
  nNegative: number;
  nZero: number;
  /** n⁺ + n⁻: the number of signed differences the test uses. */
  n: number;
  alternative: Alternative;
  /** The p-value for the chosen alternative. */
  p: number;
  pTwoSided: number;
  pLess: number;
  pGreater: number;
  proportionPositive: number;
  proportionLow: number;
  proportionHigh: number;
  level: number;
  /** Median of the differences plus `shift` (the hypothesised median in one-sample mode). */
  median: number;
  medianInterval: MedianInterval | null;
  /** Normal approximation with a continuity correction, for comparison. */
  z: number;
  pNormal: number;
}

export type SignTestOutcome =
  | { ok: true; result: SignTestResult }
  | { ok: false; reason: "too-few-values" | "all-zero" };

/** P(X ≥ k) and P(X ≤ k) for Binomial(n, ½), as {lower, upper}. */
function halfTails(k: number, n: number): { lower: number; upper: number } {
  return { lower: binomialCdf(k, n, 0.5), upper: binomialUpper(k, n, 0.5) };
}

/** The narrowest non-interpolated median interval whose coverage reaches `level`. */
export function medianInterval(
  values: readonly number[],
  level: number
): MedianInterval | null {
  const sortedValues = [...values].sort((a, b) => a - b);
  const total = sortedValues.length;
  for (let k = Math.floor(total / 2); k >= 1; k--) {
    const coverage = 1 - 2 * binomialCdf(k - 1, total, 0.5);
    if (coverage >= level - 1e-12) {
      return {
        lower: sortedValues[k - 1],
        upper: sortedValues[total - k],
        coverage,
        k,
      };
    }
  }
  return null;
}

export function signTest(
  differences: readonly number[],
  options: { alternative?: Alternative; level?: number; shift?: number } = {}
): SignTestOutcome {
  const alternative = options.alternative ?? "two-sided";
  const level = options.level ?? 0.95;
  const shift = options.shift ?? 0;
  const total = differences.length;
  if (total < 1) {
    return { ok: false, reason: "too-few-values" };
  }
  const nPositive = differences.filter((d) => d > 0).length;
  const nNegative = differences.filter((d) => d < 0).length;
  const n = nPositive + nNegative;
  if (n === 0) {
    return { ok: false, reason: "all-zero" };
  }
  const { lower, upper } = halfTails(nPositive, n);
  const pTwoSided = Math.min(1, 2 * Math.min(lower, upper));
  const pChosen = {
    "two-sided": pTwoSided,
    less: lower,
    greater: upper,
  }[alternative];
  const [low, high] = clopperPearson(nPositive, n, level);
  const z = (Math.abs(nPositive - n / 2) - 0.5) / (Math.sqrt(n) / 2);
  const interval = medianInterval(differences, level);
  return {
    ok: true,
    result: {
      total,
      nPositive,
      nNegative,
      nZero: total - n,
      n,
      alternative,
      p: pChosen,
      pTwoSided,
      pLess: lower,
      pGreater: upper,
      proportionPositive: nPositive / n,
      proportionLow: low,
      proportionHigh: high,
      level,
      median: median(differences) + shift,
      medianInterval: interval && {
        ...interval,
        lower: interval.lower + shift,
        upper: interval.upper + shift,
      },
      z: Math.max(0, z),
      pNormal: Math.min(1, 2 * normalUpper(Math.max(0, z))),
    },
  };
}
